import { useEffect, useRef } from 'react';

import { MAX_FRAME_SECONDS, SCROLL_SPEED_PX_PER_SEC, TILE_GAP } from '../../constants';
import type { WinnerTileVM } from '../../types';
import { WinnerTile } from '../WinnerTile/WinnerTile';

import styles from './WinnerConveyor.module.scss';

interface WinnerConveyorProps {
  tiles: WinnerTileVM[];
  // Viewport width, measured by the parent banner (drives re-measure on resize).
  containerWidth: number;
}

export function WinnerConveyor({ tiles, containerWidth }: WinnerConveyorProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || tiles.length === 0 || containerWidth === 0) {
      return undefined;
    }

    const contentWidth = track.scrollWidth;
    if (contentWidth <= 0) return undefined;

    let raf = 0;
    let lastTs = 0;
    let offset = 0;

    const frame = (ts: number) => {
      if (lastTs === 0) lastTs = ts;
      const dt = Math.min((ts - lastTs) / 1000, MAX_FRAME_SECONDS);
      lastTs = ts;

      offset = (offset + SCROLL_SPEED_PX_PER_SEC * dt) % contentWidth;
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [tiles.length, containerWidth]);

  return (
    <div
      ref={trackRef}
      className={styles.track}
      style={{ '--winner-tile-gap': `${TILE_GAP}px` } as React.CSSProperties}
    >
      {tiles.map((tile) => (
        <WinnerTile key={tile.winId} tile={tile} />
      ))}
    </div>
  );
}
